import { PromoPanelEvents } from "@nolimitcity/promo-panel/bin/events/PromoPanelEvents";
import { GameModel } from "@nolimitcity/slot-game/bin/model/GameModel";
import { APIFeatureBetEventData } from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import { APIBetType } from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import { APIEvent } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import { ApiPlugin } from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";

import { EventsManager, GameEvents } from "../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../abstracts/flow/AbstractGameMode";
import { ColorSet, log } from "../../abstracts/utils/AbstractConsole";
import { reelsConfig } from "../UCConfig";
import { UgliestCatch } from "../UgliestCatch";
import { isFish } from "../modules/Reels/symbols/UCSymbol";
import { UCEvents, UCSymbolNames } from "../types/UCEnums";
import { UCGameData, UCInitData } from "../types/UCInterfaces";

export class UCModel extends GameModel<UCInitData, UCGameData> {
    public isRestore: boolean = false;
    public buyFeatureName: string = ``;
    public mysteryMap: boolean[] = [];
    public hasFishesToCollect: boolean = false;
    public fishesToCollect: boolean[][] = [];
    public activeWildsPositions: boolean[][] = [];
    public finalWinningPosition: boolean[][] = [];
    public isLuckyDraw: boolean = false;
    public isXbet: boolean = false;

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);

        api.events.on(`featureName`, (data: string) => {
            this.buyFeatureName = data;
        });

        UgliestCatch.apiPlugin.events.on(APIEvent.SELECTED_FEATURE_BET_CHANGED, () => {
            const activeFeature = UgliestCatch.apiPlugin.betFeatureController.getActiveBetFeature();

            if (activeFeature !== undefined && activeFeature.name === `BOOSTED_BET`) {
                console.log(activeFeature);
                this.isXbet = true; //sau trimiti event direct
                EventsManager.trigger(GameEvents.ON_XWAYS_BUTTON_CLICK, true);
            } else if (
                this.isXbet &&
                (activeFeature === undefined || activeFeature.name !== `BOOSTED_BET`)
            ) {
                this.isXbet = false; //sau trimiti event direct
                EventsManager.trigger(GameEvents.ON_XWAYS_BUTTON_CLICK, false);
                EventsManager.trigger(UCEvents.CLOSE_BLOCKERS);
            }
        });

        UgliestCatch.apiPlugin.events.on(PromoPanelEvents.PROMO_PANEL_CLOSED, () => {
            EventsManager.trigger(GameEvents.ON_XWAYS_BUTTON_CLICK, false);
        });

        UgliestCatch.apiPlugin.events.on(APIEvent.BET, (data: APIFeatureBetEventData) => {
            this.isLuckyDraw = data.playerInteraction?.featureName === `LUCKY_DRAW`;

            if (
                data.type == APIBetType.FEATURE_BET &&
                data.playerInteraction.featureName !== `BOOSTED_BET`
            ) {
                EventsManager.trigger(GameEvents.ON_FREE_BETS_START);
            }

            if (data.featureName) {
                this.buyFeatureName = data.featureName;
            }
        });

        UgliestCatch.apiPlugin.events.on(
            APIEvent.ACTION_SPINS_BET,
            (data: APIFeatureBetEventData) => {
                this.isLuckyDraw = data.playerInteraction?.featureName === `LUCKY_DRAW`;
                if (data.featureName) {
                    this.buyFeatureName = data.featureName;
                }
            }
        );
    }

    private _currentSymbols: string[][] = [];

    public set currentSymbols(symbols: string[][]) {
        symbols.forEach((reel, reelIndex) => {
            this._currentSymbols[reelIndex] = reel;
        });
    }

    private setInitialSymbols(): void {
        const initialSymbols: string[][] = [];

        const clonedReels = [...this.initData.reels];

        for (let index: number = 0; index < reelsConfig.reelsNumber; index++) {
            initialSymbols[index] = clonedReels[index];
        }

        this.currentSymbols = initialSymbols;
    }

    protected onInit(data: UCInitData): void {
        log(`init data`, ColorSet.DARK_RED, data);

        super.onInit(data);

        this.isRestore = data.isRestoreState;

        this.setInitialSymbols();
    }

    protected onGame(data: UCGameData): void {
        log(`new data`, ColorSet.RED, data);

        // ! remember to remove this
        // data.reels[0][0] = UCSymbolNames.MYSTERY;

        // ! remember to remove this

        this.isRestore = data.isRestoreState !== undefined ? data.isRestoreState : false;

        this.mysteryMap = [];
        this.activeWildsPositions = [];
        this.fishesToCollect = [];
        this.hasFishesToCollect = false;

        data.reels.forEach((reel, reelIndex) => {
            this.mysteryMap[reelIndex] = false;
            this.fishesToCollect[reelIndex] = [];

            reel.forEach((symbol, symbolIndex) => {
                this.fishesToCollect[reelIndex][symbolIndex] = false;

                if (data.extraPositionsOpen[reelIndex] && symbol === UCSymbolNames.MYSTERY) {
                    this.mysteryMap[reelIndex] = true;
                }

                let finalSymbolName: string =
                    symbol === UCSymbolNames.MYSTERY &&
                    isFish(data.evaluatedArea[reelIndex][symbolIndex] as UCSymbolNames)
                        ? data.evaluatedArea[reelIndex][symbolIndex]
                        : symbol;

                if (isFish(finalSymbolName as UCSymbolNames)) {
                    if (
                        data.extraPositionsOpen[reelIndex] ||
                        (symbolIndex > 0 && symbolIndex < 4) ||
                        symbol === UCSymbolNames.MYSTERY
                    ) {
                        this.hasFishesToCollect = true;
                        this.fishesToCollect[reelIndex][symbolIndex] = true;
                    }
                }
            });
        });

        let tempHasFishesToCatch: boolean = false;

        data.reels.forEach((reel, reelIndex) => {
            this.activeWildsPositions[reelIndex] = [];

            reel.forEach((symbol, symbolIndex) => {
                this.activeWildsPositions[reelIndex][symbolIndex] = false;

                if (data.extraPositionsOpen[reelIndex]) {
                    if (
                        (symbolIndex === 0 ||
                            symbolIndex === 4 ||
                            data.mode !== AbstractGameMode.NORMAL) &&
                        symbol === UCSymbolNames.WILD &&
                        this.hasFishesToCollect
                    ) {
                        this.activeWildsPositions[reelIndex][symbolIndex] = true;
                        tempHasFishesToCatch = true;
                    }
                } else {
                    if (
                        symbolIndex !== 0 &&
                        symbolIndex !== 4 &&
                        data.mode !== AbstractGameMode.NORMAL &&
                        symbol === UCSymbolNames.WILD &&
                        this.hasFishesToCollect
                    ) {
                        this.activeWildsPositions[reelIndex][symbolIndex] = true;
                        tempHasFishesToCatch = true;
                    }
                }
            });
        });

        this.hasFishesToCollect = tempHasFishesToCatch;

        this.finalWinningPosition = JSON.parse(
            JSON.stringify(data.allWinningPositions !== undefined ? data.allWinningPositions : [])
        );

        if (this.hasFishesToCollect) {
            this.finalWinningPosition.forEach((reel, reelIndex) => {
                reel.forEach((symbol, symbolIndex) => {
                    if (UgliestCatch.model.activeWildsPositions[reelIndex][symbolIndex]) {
                        this.finalWinningPosition[reelIndex][symbolIndex] = true;
                    }
                });
            });
        }

        super.onGame(data);
    }

    public getBetPrice(): number {
        const hasFreeBets: boolean = UgliestCatch.apiPlugin.freeBets.hasFreeBets();
        const bet: string = UgliestCatch.apiPlugin.freeBets.getBet();
        const level: string = UgliestCatch.apiPlugin.betLevel.getLevel();

        const betLevel: string = hasFreeBets ? bet : level;
        let price: number = UgliestCatch.model.gameData?.playedBetValue || parseFloat(betLevel);

        if (this.data.wasFeatureBuy || this.data.enhancedBet) {
            for (let index = 0; index < this.initData.featureBuyTimesBetValue.length; index++) {
                if (this.initData.featureBuyTimesBetValue[index].name === this.buyFeatureName) {
                    price *= this.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }

        return price;
    }

    private getAccumulatedRoundWin(): number {
        const acWinInit = this.initData.accumulatedRoundWin;
        const acWinGame = this.gameData ? this.gameData.accumulatedRoundWin : 0;

        return this.isRestore ? acWinInit : acWinGame;
    }

    private getSpinWin(): number {
        return this.isRestore ? this.initData.totalSpinWinnings : this.gameData.totalSpinWinnings;
    }

    public isWinBelowStake(isAccumulated: boolean = true): boolean {
        const price = this.getBetPrice();
        const accumulatedRoundWin = isAccumulated
            ? this.getAccumulatedRoundWin()
            : this.getSpinWin();

        return (
            UgliestCatch.apiPlugin.gameClientConfiguration.belowStakeWinRestriction &&
            accumulatedRoundWin <= price
        );
    }
}
